import { apiRequest, apiRoutes } from '../../api';
import { useState, useEffect, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import ChildCareOutlinedIcon from '@mui/icons-material/ChildCareOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import MosqueOutlinedIcon from '@mui/icons-material/MosqueOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import DirectionsWalkOutlinedIcon from '@mui/icons-material/DirectionsWalkOutlined';
import DirectionsBusOutlinedIcon from '@mui/icons-material/DirectionsBusOutlined';
import SubwayOutlinedIcon from '@mui/icons-material/SubwayOutlined';
import LocalTaxiOutlinedIcon from '@mui/icons-material/LocalTaxiOutlined';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import AttractionsOutlinedIcon from '@mui/icons-material/AttractionsOutlined';
import ArchitectureOutlinedIcon from '@mui/icons-material/ArchitectureOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './PropertyWizard.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const FEATURE_ICON_RULES = [
  { key: 'park', pattern: /park|green|پارک|فضای سبز/, Icon: ParkOutlinedIcon, color: '#2e7d32' },
  { key: 'health', pattern: /health|hospital|clinic|درمان|بیمارستان/, Icon: LocalHospitalOutlinedIcon, color: '#d32f2f' },
  { key: 'preschool', pattern: /preschool|kindergarten|مهد|پیش دبستان/, Icon: ChildCareOutlinedIcon, color: '#ad1457' },
  { key: 'school', pattern: /school|مدرسه|مدارس/, Icon: SchoolOutlinedIcon, color: '#1565c0' },
  { key: 'mosque', pattern: /musque|mosque|مسجد/, Icon: MosqueOutlinedIcon, color: '#00897b' },
  { key: 'gym', pattern: /gym|sport|باشگاه|ورزش/, Icon: FitnessCenterOutlinedIcon, color: '#ef6c00' },
  { key: 'walk', pattern: /walk|pathway|پیاده/, Icon: DirectionsWalkOutlinedIcon, color: '#6d4c41' },
  { key: 'bus', pattern: /bus|اتوبوس/, Icon: DirectionsBusOutlinedIcon, color: '#5e35b1' },
  { key: 'metro', pattern: /metro|subway|مترو/, Icon: SubwayOutlinedIcon, color: '#3949ab' },
  { key: 'taxi', pattern: /taxi|تاکسی/, Icon: LocalTaxiOutlinedIcon, color: '#f9a825' },
  { key: 'route', pattern: /highway|route|depth|centrality|بزرگراه|مسیر/, Icon: AltRouteOutlinedIcon, color: '#546e7a' },
  { key: 'shopping', pattern: /shop|cbd|commercial|خرید|تجاری/, Icon: ShoppingBasketOutlinedIcon, color: '#c2185b' },
  { key: 'recreation', pattern: /recreation|leisure|تفریح|فراغت/, Icon: AttractionsOutlinedIcon, color: '#7b1fa2' },
  { key: 'property', pattern: /property|ملک/, Icon: HomeWorkOutlinedIcon, color: '#315c48' },
  { key: 'urban', pattern: /zonn|zone|floor|builtarea|far|street_width|deprecated|depricated|traffic|پهنه|طبقه|ساخت|معبر|فرسوده|ترافیک/, Icon: ArchitectureOutlinedIcon, color: '#455a64' },
  { key: 'score', pattern: /score|index|شاخص|امتیاز/, Icon: InsightsOutlinedIcon, color: '#00796b' },
];

const DEFAULT_FEATURE_ICON = {
  key: 'place',
  Icon: PlaceOutlinedIcon,
  color: '#315c48',
};

const markerIconCache = new Map();

const propertyMarkerIcon = L.divIcon({
  className: 'property-map-marker',
  html: `<div class="property-map-marker__pulse"></div><div class="property-map-marker__pin">${renderToStaticMarkup(<HomeWorkOutlinedIcon aria-hidden="true" />)}</div>`,
  iconSize: [48, 52],
  iconAnchor: [24, 48],
  popupAnchor: [0, -46],
  tooltipAnchor: [0, -42],
});

const getFeatureVisual = (...values) => {
  const searchableText = values.filter(Boolean).join(' ').toLocaleLowerCase('fa');
  return FEATURE_ICON_RULES.find(rule => rule.pattern.test(searchableText)) || DEFAULT_FEATURE_ICON;
};

const createFeatureMarkerIcon = (...values) => {
  const visual = getFeatureVisual(...values);

  if (!markerIconCache.has(visual.key)) {
    const IconComponent = visual.Icon;
    const iconMarkup = renderToStaticMarkup(<IconComponent aria-hidden="true" />);

    markerIconCache.set(visual.key, L.divIcon({
      className: 'poi-map-marker',
      html: `<div class="poi-map-marker__pin" style="--poi-color: ${visual.color}">${iconMarkup}</div>`,
      iconSize: [36, 42],
      iconAnchor: [18, 40],
      popupAnchor: [0, -36],
    }));
  }

  return markerIconCache.get(visual.key);
};

function FeatureIcon({ featureKey, label, featureType, poiType }) {
  const visual = getFeatureVisual(featureKey, label, featureType, poiType);
  const IconComponent = visual.Icon;

  return (
    <span
      className="feature-icon-badge"
      style={{ '--feature-color': visual.color }}
      aria-hidden="true"
    >
      <IconComponent />
    </span>
  );
}

// Geospatial Analysis Step Component
function GeospatialAnalysisStep({ onSubmit, initialData = {}, onPreviousStep, locationData = {}, propertyId }) {
  const [analysisResults, setAnalysisResults] = useState(initialData.analysisResults || {});
  const [geoFeatures, setGeoFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);

  // Get coordinates from location data
  const lat = locationData.location?.coordinates?.[1] || locationData.lat;
  const lon = locationData.location?.coordinates?.[0] || locationData.lon;

  // Fetch available geo features
  const fetchGeoFeatures = async () => {
    try {
      const token = localStorage.getItem('access_token');
      // Fetch all feature types including new ones
      const response = await apiRequest(
        apiRoutes.geo.features,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { feature_types: 'count,nearest_distance,score,custom,numerical,categorical,boolian' }
        }
      );

      if (response.ok) {
        const features = await response.json();
        setGeoFeatures(features);
      } else {

      }
    } catch (err) {

    }
  };

  // Load geospatial analysis data
  const loadAnalysisData = async () => {
    if (!lat || !lon) {
      setError('مختصات جغرافیایی در دسترس نیست. لطفاً ابتدا موقعیت ملک را مشخص کنید.');
      return;
    }

    if (geoFeatures.length === 0) {
      setError('هیچ ویژگی جغرافیایی برای تحلیل یافت نشد.');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysisProgress(0);

    try {
      const token = localStorage.getItem('access_token');
      const results = {};

      // Fetch parcel zoning data and add as separate features
      const parcelResponse = await apiRequest(
        apiRoutes.geo.parcelZoning,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { lat, lon }
        }
      );

      if (parcelResponse.ok) {
        const parcelData = await parcelResponse.json();

        // Add parcel zoning data as separate analysis results using existing GeoFeatures
        if (parcelData.found) {
          // Find the GeoFeature records for parcel zoning features
          const zoningFeature = geoFeatures.find(f => f.name === 'zonning');
          const deprecatedFeature = geoFeatures.find(f => f.name === 'in_depricated_area');
          const streetWidthFeature = geoFeatures.find(f => f.name === 'street_width');

          // Find the GeoFeature records for zoning regulation features
          const zoneDescFeature = geoFeatures.find(f => f.name === 'zone_desc');
          const maxFarFeature = geoFeatures.find(f => f.name === 'max_far');
          const maxFloorFeature = geoFeatures.find(f => f.name === 'max_n_floor');
          const maxBuiltareaFeature = geoFeatures.find(f => f.name === 'max_builtarea_ratio');

          // Zoning feature - using GeoFeature ID and numeric value
          if (zoningFeature) {
            results.zonning = {
              geo_feature_id: zoningFeature.id,
              value: 1, // Use 1 to indicate zoning data is available
              score: null,
              unit: 'زون',
              raw_data: { zonning: parcelData.zonning, parcel_id: parcelData.parcel_id },
              notes: `زون: ${parcelData.zonning || 'نامشخص'}`,
              description: zoningFeature.description || 'زون برنامه‌ریزی شهری',
              feature_type: zoningFeature.feature_type,
              name: zoningFeature.name,
              zone_value: parcelData.zonning
            };
          }

          // Deprecated area feature - using GeoFeature ID
          if (deprecatedFeature) {
            results.in_depricated_area = {
              geo_feature_id: deprecatedFeature.id,
              value: parcelData.in_depricated_area ? 1 : 0,
              score: null,
              unit: 'وضعیت',
              raw_data: { in_depricated_area: parcelData.in_depricated_area, parcel_id: parcelData.parcel_id },
              notes: `بافت فرسوده: ${parcelData.in_depricated_area ? 'بله' : 'خیر'}`,
              description: deprecatedFeature.description || 'قرارگیری در بافت فرسوده',
              feature_type: deprecatedFeature.feature_type,
              name: deprecatedFeature.name,
              is_deprecated: parcelData.in_depricated_area
            };
          }

          // Street width feature - using GeoFeature ID
          if (streetWidthFeature) {
            results.street_width = {
              geo_feature_id: streetWidthFeature.id,
              value: parseFloat(parcelData.street_width) || 0,
              score: null,
              unit: 'متر',
              raw_data: { street_width: parcelData.street_width, parcel_id: parcelData.parcel_id },
              notes: `عرض معبر: ${parcelData.street_width || 'نامشخص'} متر`,
              description: streetWidthFeature.description || 'عرض معبر مجاور',
              feature_type: streetWidthFeature.feature_type,
              name: streetWidthFeature.name,
              width_value: parcelData.street_width
            };
          }

          // Zone Description feature
          if (zoneDescFeature) {
            results.zone_desc = {
              geo_feature_id: zoneDescFeature.id,
              value: parcelData.zone_desc || 0, // Use 1 if description exists, 0 if not
              score: null,
              unit: 'توضیحات',
              raw_data: { zone_desc: parcelData.zone_desc, parcel_id: parcelData.parcel_id },
              notes: `توضیحات زون: ${parcelData.zonedesc || 'نامشخص'}`,
              description: zoneDescFeature.description || 'توضیحات زون',
              feature_type: zoneDescFeature.feature_type,
              name: zoneDescFeature.name,
              zone_desc_value: parcelData.zone_desc
            };
          }

          // Maximum FAR feature
          if (maxFarFeature) {
            results.max_far = {
              geo_feature_id: maxFarFeature.id,
              value: parseFloat(parcelData.max_far) || 0,
              score: null,
              unit: 'ضریب',
              raw_data: { max_far: parcelData.max_far, parcel_id: parcelData.parcel_id },
              notes: `حداکثر ضریب کف فروشی: ${parcelData.max_far || 'نامشخص'}`,
              description: maxFarFeature.description || 'حداکثر ضریب کف فروشی',
              feature_type: maxFarFeature.feature_type,
              name: maxFarFeature.name,
              max_far_value: parcelData.max_far
            };
          }

          // Maximum floors feature
          if (maxFloorFeature) {
            results.max_n_floor = {
              geo_feature_id: maxFloorFeature.id,
              value: parseInt(parcelData.max_n_floor) || 0,
              score: null,
              unit: 'طبقه',
              raw_data: { max_n_floor: parcelData.max_n_floor, parcel_id: parcelData.parcel_id },
              notes: `حداکثر تعداد طبقات: ${parcelData.max_n_floor || 'نامشخص'}`,
              description: maxFloorFeature.description || 'حداکثر تعداد طبقات',
              feature_type: maxFloorFeature.feature_type,
              name: maxFloorFeature.name,
              max_floor_value: parcelData.max_n_floor
            };
          }

          // Maximum built area ratio feature
          if (maxBuiltareaFeature) {
            results.max_builtarea_ratio = {
              geo_feature_id: maxBuiltareaFeature.id,
              value: parseFloat(parcelData.max_builtarea_ratio) || 0,
              score: null,
              unit: 'درصد',
              raw_data: { max_builtarea_ratio: parcelData.max_builtarea_ratio, parcel_id: parcelData.parcel_id },
              notes: `حداکثر درصد اشغال: ${parcelData.max_builtarea_ratio || 'نامشخص'}%`,
              description: maxBuiltareaFeature.description || 'حداکثر درصد اشغال',
              feature_type: maxBuiltareaFeature.feature_type,
              name: maxBuiltareaFeature.name,
              max_builtarea_value: parcelData.max_builtarea_ratio
            };
          }

        } else {
          // No parcel found at this location — skip parcel features
        }
      }

      // Process each geo feature
      for (let i = 0; i < geoFeatures.length; i++) {
        const feature = geoFeatures[i];
        setAnalysisProgress(((i + 1) / geoFeatures.length) * 100);

        // Skip parcel zoning features as they are already processed above
        if (['zonning', 'in_depricated_area', 'street_width', 'zone_desc', 'max_far', 'max_n_floor', 'max_builtarea_ratio'].includes(feature.name)) {
          continue;
        }

        try {
          // score type: read from hex_grid spatial overlay (fetched once below the loop)
          if (feature.feature_type === 'score') {
            // value will be filled in from hexScores after the loop
            continue;
          }

          // custom / boolian / categorical / numerical types: skip POI API — values come from parcel or hex data
          if (['custom', 'boolian', 'categorical', 'numerical'].includes(feature.feature_type)) {
            continue;
          }

          // For count and nearest_distance, make API calls
          const params = {
            lat,
            lon,
            type_ids: (feature.poi_type_ids || []).join(',')
          };

          if (feature.radius) params.radius = feature.radius;
          if (feature.count) params.max_count = feature.count;

          const featureResponse = await apiRequest(
            apiRoutes.geo.poisDistance,
            {
              headers: { 'Authorization': `Bearer ${token}` },
              params
            }
          );

          if (featureResponse.ok) {
            const featureData = await featureResponse.json();

            // Process the result based on feature type
            let value = 0;
            let unit = '';
            let notes = '';

            if (feature.feature_type === 'count') {
              value = featureData.query?.total_found || 0;
              unit = 'تعداد';
              notes = `تعداد ${feature.name} در شعاع ${feature.radius || 1000} متری`;
            } else if (feature.feature_type === 'nearest_distance') {
              if (featureData.results && featureData.results.length > 0) {
                // Find the nearest facility
                const nearest = featureData.results[0];
                // Distance might be in the distance field or calculated from coordinates
                value = nearest.distance || nearest.distance_m || 0;
                unit = 'متر';
                notes = `فاصله تا نزدیک‌ترین ${feature.name}: ${nearest.name || 'نامشخص'}`;
              } else {
                value = -1; // No facility found
                unit = 'متر';
                notes = `هیچ ${feature.name} در محدوده یافت نشد`;
              }
            }

            results[feature.name] = {
              geo_feature_id: feature.id,
              value: value,
              score: null, // Can be calculated later if needed
              unit: unit,
              raw_data: featureData,
              notes: notes,
              feature_type: feature.feature_type,
              description: feature.description || feature.name,
              name: feature.name
            };

          } else {

            // Still add a result with error information
            results[feature.name] = {
              geo_feature_id: feature.id,
              value: -1,
              score: null,
              unit: 'خطا',
              raw_data: { error: `HTTP ${featureResponse.status}` },
              notes: `خطا در دریافت اطلاعات ${feature.name}`,
              feature_type: feature.feature_type,
              description: feature.description || feature.name,
              name: feature.name
            };
          }

        } catch (featureErr) {

        }

        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Fetch hex_grid scores for 'score' type features
      const hexResponse = await apiRequest(
        apiRoutes.geo.hexScores,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { lat, lon }
        }
      );
      if (hexResponse.ok) {
        const hexData = await hexResponse.json();
        if (hexData.found) {
          geoFeatures
            .filter(f => f.feature_type === 'score')
            .forEach(feature => {
              const hexValue = hexData.scores[feature.name];
              if (hexValue !== undefined && hexValue !== null) {
                results[feature.name] = {
                  geo_feature_id: feature.id,
                  value: hexValue,
                  score: hexValue,
                  unit: '',
                  raw_data: { hex_grid: true, raw_score: hexValue },
                  notes: 'مقدار از شبکه هگزاگونال',
                  feature_type: 'score',
                  description: feature.description || feature.name,
                  name: feature.name,
                };
              }
            });
        }
      }
      setAnalysisResults({ ...results });

    } catch (err) {
      setError('خطا در دریافت اطلاعات تحلیل مکانی: ' + err.message);
    } finally {
      setLoading(false);
      setAnalysisProgress(0);
    }
  };

  // Collect all POIs from analysis results for map display
  const allPOIs = useMemo(() => {
    const pois = [];
    Object.values(analysisResults).forEach((result) => {
      if (result.raw_data && result.raw_data.results && Array.isArray(result.raw_data.results)) {
        result.raw_data.results.forEach((poi) => {
          if (poi.location && Array.isArray(poi.location) && poi.location.length === 2) {
            pois.push({
              ...poi,
              featureType: result.feature_type,
              featureName: Object.keys(analysisResults).find(key => analysisResults[key] === result)
            });
          }
        });
      }
    });
    return pois;
  }, [analysisResults]);

  // Group analysis results by feature_type
  const groupedResults = useMemo(() => {
    const grouped = {
      count: [],
      nearest_distance: [],
      score: [],
      // custom, boolian, categorical, numerical all rendered in the same "urban rules" section
      custom: [],
    };

    Object.entries(analysisResults).forEach(([key, result]) => {
      if (result.feature_type === 'count') {
        grouped.count.push({ key, ...result });
      } else if (result.feature_type === 'nearest_distance') {
        grouped.nearest_distance.push({ key, ...result });
      } else if (result.feature_type === 'score') {
        grouped.score.push({ key, ...result });
      } else if (['custom', 'boolian', 'categorical', 'numerical'].includes(result.feature_type)) {
        grouped.custom.push({ key, ...result });
      }
    });

    return grouped;
  }, [analysisResults]);

  // Load geo features when component mounts
  useEffect(() => {
    fetchGeoFeatures();
  }, []);

  // Load analysis data when geo features are loaded and coordinates are available
  useEffect(() => {
    if (lat && lon && geoFeatures.length > 0) {
      loadAnalysisData();
    }
  }, [lat, lon, geoFeatures]);

  const handleSubmit = async () => {

    // Save analysis results to backend
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.geo.saveAnalysis, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          draft_id: propertyId,
          analysisResults: analysisResults
        })
      });

      if (response.ok) {
        // Submit to parent component
        onSubmit({
          analysisResults,
          completed: true
        });
      } else {
        let errorMessage = 'خطای نامشخص';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          const errorText = await response.text();
          if (errorText.includes('<!DOCTYPE')) {
            errorMessage = 'Server returned HTML error page - check backend logs';
          } else {
            errorMessage = errorText || errorMessage;
          }
        }
        setError('خطا در ذخیره تحلیل مکانی: ' + errorMessage);
      }
    } catch (err) {

      setError('خطا در ذخیره تحلیل مکانی: ' + err.message);
    }
  };

  return (
    <div className="geospatial-analysis-step">
      {/* Step Header */}
      <div className="step-header">
        <h3>تحلیل کیفیت مکانی</h3>
        <p>تحلیل کیفیت محله و دسترسی‌های جغرافیایی ملک شما</p>
      </div>

      {/* Compact Layout: Controls and Map Side by Side */}
      <div className="geospatial-layout">
        {/* Left Column: Controls and Results */}
        <div className="geospatial-left-column">
          {!lat || !lon ? (
            <div className="no-coordinates-warning-compact">
              لطفاً ابتدا موقعیت ملک را در مرحله اول مشخص کنید.
            </div>
          ) : null}

          {/* Analysis Results - Compact Grid */}
          {Object.keys(analysisResults).length > 0 && (
            <div className="analysis-results-section-compact">
              <div className="results-header-compact">
                <h4>نتایج تحلیل ({Object.keys(analysisResults).length})</h4>
              </div>

              {/* Summary Statistics - Compact */}
              <div className="analysis-summary-compact">
                <div className="summary-item-compact">
                  <span className="summary-label-compact">تحلیل شده:</span>
                  <span className="summary-value-compact">{Object.keys(analysisResults).length}</span>
                </div>
                <div className="summary-item-compact">
                  <span className="summary-label-compact">در دسترس:</span>
                  <span className="summary-value-compact">
                    {Object.values(analysisResults).filter(r => r.value > 0 && r.value !== -1).length}
                  </span>
                </div>
              </div>

              {/* Count Type Features */}
              {groupedResults.count.length > 0 && (
                <div className="result-category-section">
                  <div className="category-header">
                    <span className="category-title">تعداد امکانات ({groupedResults.count.length})</span>
                  </div>
                  <div className="results-grid-compact">
                    {groupedResults.count.map(({ key, ...featureData }) => (
                      <div
                        key={key}
                        className="result-card-compact"
                        data-type="count"
                      >
                        <FeatureIcon
                          featureKey={key}
                          label={featureData.description || featureData.name}
                          featureType={featureData.feature_type}
                        />
                        <div className="card-content-compact">
                          <div className="card-title-compact">
                            {featureData.description || featureData.name || key}
                          </div>
                          <div className="card-value-compact">
                            <span className={`value-compact ${featureData.value === -1 ? 'not-found-compact' : ''}`}>
                              {featureData.value === -1 ? '—' : Math.round(featureData.value)}
                            </span>
                            {featureData.value !== -1 && (
                              <span className="unit-compact">{featureData.unit}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Nearest Distance Type Features */}
              {groupedResults.nearest_distance.length > 0 && (
                <div className="result-category-section">
                  <div className="category-header">
                    <span className="category-title">فاصله تا نزدیک‌ترین ({groupedResults.nearest_distance.length})</span>
                  </div>
                  <div className="results-grid-compact">
                    {groupedResults.nearest_distance.map(({ key, ...featureData }) => (
                      <div
                        key={key}
                        className="result-card-compact"
                        data-type="nearest_distance"
                      >
                        <FeatureIcon
                          featureKey={key}
                          label={featureData.description || featureData.name}
                          featureType={featureData.feature_type}
                        />
                        <div className="card-content-compact">
                          <div className="card-title-compact">
                            {featureData.description || featureData.name || key}
                          </div>
                          <div className="card-value-compact">
                            <span className={`value-compact ${featureData.value === -1 ? 'not-found-compact' : ''}`}>
                              {featureData.value === -1 ? '—' : Math.round(featureData.value)}
                            </span>
                            {featureData.value !== -1 && (
                              <span className="unit-compact">{featureData.unit}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Score Type Features */}
              {groupedResults.score.length > 0 && (
                <div className="result-category-section">
                  <div className="category-header">
                    <span className="category-title">امتیاز نرمال‌شده ({groupedResults.score.length})</span>
                  </div>
                  <div className="results-grid-compact">
                    {groupedResults.score.map(({ key, ...featureData }) => (
                      <div
                        key={key}
                        className="result-card-compact"
                        data-type="score"
                      >
                        <FeatureIcon
                          featureKey={key}
                          label={featureData.description || featureData.name}
                          featureType={featureData.feature_type}
                        />
                        <div className="card-content-compact">
                          <div className="card-title-compact">
                            {featureData.description || featureData.name || key}
                          </div>
                          <div className="card-value-compact">
                            <span className="value-compact">
                              {Math.round(featureData.value)}
                            </span>
                            <span className="unit-compact">{featureData.unit}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom / Boolian / Categorical / Numerical Features — urban rules & parcel data */}
              {groupedResults.custom.length > 0 && (
                <div className="result-category-section">
                  <div className="category-header">
                    <span className="category-title">ضوابط و مقررات شهرسازی ({groupedResults.custom.length})</span>
                  </div>
                  <div className="results-grid-compact">
                    {groupedResults.custom.map(({ key, ...featureData }) => {
                      let displayValue;
                      let valueClass = 'value-compact';
                      let showUnit = true;

                      // boolian feature_type or known boolean parcel fields
                      if (featureData.feature_type === 'boolian' || key === 'in_depricated_area') {
                        const boolVal = featureData.is_deprecated ?? (featureData.value === 1);
                        displayValue = boolVal ? 'بله' : 'خیر';
                        valueClass = `value-compact ${boolVal ? 'warning' : 'success'}`;
                        showUnit = false;
                      // categorical feature_type or known text parcel fields
                      } else if (featureData.feature_type === 'categorical' || key === 'zonning') {
                        displayValue = featureData.zone_value || featureData.raw_data?.zonning || featureData.value || 'نامشخص';
                        showUnit = false;
                      } else if (key === 'zone_desc') {
                        displayValue = featureData.zone_desc_value || 'نامشخص';
                        showUnit = false;
                      } else if (key === 'street_width') {
                        displayValue = featureData.width_value ?? featureData.value ?? '—';
                      } else if (key === 'max_far') {
                        displayValue = featureData.max_far_value ?? featureData.value ?? '—';
                      } else if (key === 'max_n_floor') {
                        displayValue = featureData.max_floor_value ?? featureData.value ?? '—';
                      } else if (key === 'max_builtarea_ratio') {
                        const raw = featureData.max_builtarea_value ?? featureData.value;
                        displayValue = raw != null ? `${raw*100}%` : '—';
                        showUnit = false;
                      } else {
                        // numerical or generic custom
                        displayValue = featureData.value != null ? featureData.value : '—';
                      }

                      return (
                        <div key={key} className="result-card-compact" data-type={featureData.feature_type}>
                          <FeatureIcon
                            featureKey={key}
                            label={featureData.description || featureData.name}
                            featureType={featureData.feature_type}
                          />
                          <div className="card-content-compact">
                            <div className="card-title-compact">
                              {featureData.description || featureData.name || key}
                            </div>
                            <div className="card-value-compact">
                              <span className={valueClass}>{displayValue}</span>
                              {showUnit && featureData.unit && (
                                <span className="unit-compact">{featureData.unit}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Right Column: Map */}
        {lat && lon && (
          <div className="geospatial-map-column">
            {/* Analysis Controls */}
            <div className="analysis-controls-compact">
              <button
                type="button"
                onClick={loadAnalysisData}
                disabled={loading}
                className="refresh-analysis-button-compact"
              >
                {loading ? 'در حال تحلیل...' : 'بروزرسانی'}
              </button>

              {loading && analysisProgress > 0 && (
                <div className="analysis-progress-compact">
                  <div className="progress-bar-compact">
                    <div
                      className="progress-fill-compact"
                      style={{ width: `${analysisProgress}%` }}
                    />
                  </div>
                  <span className="progress-text-compact">{Math.round(analysisProgress)}%</span>
                </div>
              )}

              <div className="geo-features-info-compact">
                {geoFeatures.length} ویژگی
              </div>
            </div>

            <div className="map-header-compact">
              <span className="map-title">نقشه امکانات ({allPOIs.length} مورد)</span>
            </div>
            <div className="map-container-compact">
              <MapContainer
                center={[lat, lon]}
                zoom={14}
                style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* Property Location Marker */}
                <Marker
                  position={[lat, lon]}
                  icon={propertyMarkerIcon}
                  zIndexOffset={1000}
                >
                  <Tooltip permanent direction="top" offset={[0, -42]} className="property-map-label">
                    موقعیت ملک
                  </Tooltip>
                  <Popup>
                    <strong>موقعیت ملک</strong>
                    <br />
                    {Math.round(lat)}, {Math.round(lon)}
                  </Popup>
                </Marker>

                {/* All POIs Markers */}
                {allPOIs.map((poi, index) => (
                  <Marker
                    key={index}
                    position={[poi.location[0], poi.location[1]]}
                    icon={createFeatureMarkerIcon(poi.featureName, poi.type, poi.name)}
                  >
                    <Popup>
                      <div className="poi-popup-content">
                        <FeatureIcon
                          featureKey={poi.featureName}
                          label={poi.name}
                          poiType={poi.type}
                        />
                        <strong>{poi.name || 'بدون نام'}</strong>
                        <br />
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          نوع: {poi.type}
                        </span>
                        {poi.distance && (
                          <>
                            <br />
                            <span style={{ fontSize: '12px', color: '#666' }}>
                              فاصله: {Math.round(poi.distance)} متر
                            </span>
                          </>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-display-compact">
          {error}
        </div>
      )}

      {/* Submit Button */}
      <div className="geospatial-actions">
        <button
          type="button"
          onClick={handleSubmit}
          className="submit-button"
        >
          تأیید و ادامه
        </button>

        <button
          type="button"
          onClick={onPreviousStep}
          className="previous-button"
        >
          بازگشت
        </button>
      </div>
    </div>
  );
}

export default GeospatialAnalysisStep;
